import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/shared/components/ui/dialog';

type VettingDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description: ReactNode;
  /** Extra body content (e.g. a note field or an error message). */
  children?: ReactNode;
  confirmLabel: string;
  pendingLabel: string;
  isPending: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  /** Destructive actions (Decline) use red; everything else uses the bQuest green. */
  tone?: 'primary' | 'danger';
};

const CONFIRM_TONE = {
  primary: 'bg-[#026857] hover:bg-[#025246]',
  danger: 'bg-red-600 hover:bg-red-700',
};

/**
 * Vetting confirmation popup, styled like the admin "Create Partner Company"
 * popup (PartnerCompanyForm): header + close ✕ above a divider, padded body,
 * footer actions below a divider. Built on the shared Radix Dialog so Escape,
 * outside-click and focus handling still work.
 */
export function VettingDialog({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel,
  pendingLabel,
  isPending,
  confirmDisabled = false,
  onConfirm,
  tone = 'primary',
}: VettingDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isPending && onClose()}>
      {/* Hide the shared component's built-in ✕ — the header below renders its own */}
      <DialogContent className="max-w-lg gap-0 rounded-lg bg-white p-0 shadow-xl [&>button:last-child]:hidden">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <DialogTitle className="text-xl font-semibold text-gray-900">{title}</DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              disabled={isPending}
              className="rounded-md p-1 transition-colors hover:bg-gray-100 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </DialogClose>
        </div>

        <div className="space-y-4 px-6 py-4">
          <DialogDescription className="text-sm text-gray-700">{description}</DialogDescription>
          {children}

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={confirmDisabled || isPending}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${CONFIRM_TONE[tone]}`}
            >
              {isPending ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>{pendingLabel}</span>
                </>
              ) : (
                <span>{confirmLabel}</span>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
